import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  BellRingingIcon, PaperPlaneTiltIcon, WarningIcon, CheckCircleIcon, SpinnerGapIcon, LockKeyIcon,
} from "@phosphor-icons/react";
import { getClientId } from "../lib/clientId";import { useAuth } from "../context/AuthContext";
import { useWatchlist } from "../context/WatchlistContext";
import {
  fetchAlertConfig, fetchAlertSettings, saveAlertSettings, sendTestAlert, sendDeployerTestAlert, sendTestDigest, trackEvent, fetchHandleCount,
} from "../lib/api";

const PLATFORMS = [
  { id: "axiom", label: "Axiom" },
  { id: "gmgn", label: "GMGN" },
  { id: "photon", label: "Photon" },
  { id: "bullx", label: "BullX" },
  { id: "dexscreener", label: "DexScreener" },
  { id: "robinhood", label: "Robinhood" },
];

const Toggle = ({ checked, onChange, testid }) => (
  <button
    data-testid={testid}
    onClick={() => onChange(!checked)}
    className={`relative w-11 h-6 transition-colors duration-150 border ${
      checked ? "bg-neon/20 border-neon/50" : "bg-white/5 border-white/15"
    }`}
  >
    <span
      className={`absolute top-0.5 h-4 w-4 transition-all duration-150 ${
        checked ? "left-6 bg-neon" : "left-0.5 bg-white/40"
      }`}
    />
  </button>
);

const TriggerRow = ({ title, desc, checked, onChange, testid, disabled, action }) => (
  <div className={`flex items-start justify-between gap-4 py-4 border-b border-white/5 ${disabled ? "opacity-50" : ""}`}>
    <div>
      <div className="font-medium text-sm">{title}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
      {action}
    </div>
    {disabled ? (
      <span className="font-mono text-[10px] px-2 py-1 border border-white/10 text-muted-foreground shrink-0">SOON</span>
    ) : (
      <Toggle checked={checked} onChange={onChange} testid={testid} />
    )}
  </div>
);

export default function Alerts() {
  const clientId = getClientId();
  const { requireAuth } = useAuth();
  const { watchlist } = useWatchlist();

  const configQ = useQuery({ queryKey: ["alertConfig"], queryFn: fetchAlertConfig });
  const settingsQ = useQuery({ queryKey: ["alertSettings", clientId], queryFn: () => fetchAlertSettings(clientId) });

  const [chatId, setChatId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [minScore, setMinScore] = useState(75);
  const [handleMinScore, setHandleMinScore] = useState(0);
  const [handleCounts, setHandleCounts] = useState({ preview: null, base: null, handles: null });
  const [egMinScore, setEgMinScore] = useState(60);
  const [clusterMin, setClusterMin] = useState(2);
  const [millyMinSol, setMillyMinSol] = useState(5);
  const [provenMinHit, setProvenMinHit] = useState(0);
  const [digestHour, setDigestHour] = useState(13);
  const [gradMaxMcap, setGradMaxMcap] = useState(750000);
  const [watchedCreators, setWatchedCreators] = useState([]);
  const [creatorInput, setCreatorInput] = useState("");
  const addCreators = () => {
    const found = (creatorInput.match(/[1-9A-HJ-NP-Za-km-z]{32,44}/g) || []);
    if (!found.length) { toast.error("Paste a valid Solana wallet address"); return; }
    setWatchedCreators((list) => {
      const merged = Array.from(new Set([...list, ...found]));
      const added = merged.length - list.length;
      toast.success(added > 0 ? `Added ${added} wallet${added === 1 ? "" : "s"} — Save to apply` : "Already on your list");
      return merged;
    });
    setCreatorInput("");
  };
  const [robinhoodKeywords, setRobinhoodKeywords] = useState([]);
  const [platform, setPlatform] = useState("axiom");
  const [triggers, setTriggers] = useState({ threshold: true, newHighScore: true, watchlistChange: true, smartMoney: true, trackedDeploy: true, deployOnly: false, devDigest: false, radarAutoTrack: false, waiterWake: true, waiterEntry: true, tier3Confirm: true, runAlert: true, dumpAlert: true, launchAlert: true, blacklistBuy: true, alphaLaunch: true, provenDeployerLaunch: false, followedBuilderLaunch: true, watchedCreatorLaunch: true, robinhoodLaunch: true, robinhoodWallet: true, robinhoodSurge: true, holderMilestone: true, structureGate: false, botWarn: false, copyBuys: false, earlyGamble: false, fastMover: false, clusterBuy: true, millysCall: true, millyRunner: true, graduateRunner: true, cabalBuy: true, smartExit: true, winnerCobuy: true, qualityAlert: true, organicRunner: true, convictionAlert: true, watchlistRunner: true, runnerAlert: true, themeAlert: true, techAlert: true, techFreshOnly: false, candleCarry: true, candleLaunch: false, earlyRunner: true, skipLowQuality: true, breakoutAlert: true, convictionDigest: true, githubActive: true, morningCall: true, tgSignal: true });

  useEffect(() => {
    if (settingsQ.data) {
      setChatId(settingsQ.data.chatId || "");
      setEnabled(settingsQ.data.enabled || false);
      setMinScore(settingsQ.data.minScore ?? 75);
      setHandleMinScore(settingsQ.data.handleMinScore ?? 0);      setEgMinScore(settingsQ.data.egMinScore ?? 60);
      setClusterMin(settingsQ.data.clusterMin ?? 2);
      setMillyMinSol(settingsQ.data.millyMinSol ?? 5);
      setProvenMinHit(settingsQ.data.provenMinHit ?? 0);
      setDigestHour(settingsQ.data.digestHour ?? 13);
      setGradMaxMcap(settingsQ.data.gradMaxMcap ?? 750000);
      setPlatform(settingsQ.data.platform || "axiom");
      setWatchedCreators(settingsQ.data.watchedCreators || []);
      if (settingsQ.data.robinhoodKeywords?.length) setRobinhoodKeywords(settingsQ.data.robinhoodKeywords);
      setTriggers({ threshold: true, newHighScore: true, watchlistChange: true, smartMoney: true, trackedDeploy: true, deployOnly: false, devDigest: false, radarAutoTrack: false, waiterWake: true, waiterEntry: true, tier3Confirm: true, runAlert: true, dumpAlert: true, launchAlert: true, blacklistBuy: true, alphaLaunch: true, provenDeployerLaunch: false, followedBuilderLaunch: true, watchedCreatorLaunch: true, robinhoodLaunch: true, robinhoodWallet: true, robinhoodSurge: true, holderMilestone: true, structureGate: false, botWarn: false, copyBuys: false, earlyGamble: false, fastMover: false, clusterBuy: true, millysCall: true, millyRunner: true, graduateRunner: true, cabalBuy: true, smartExit: true, winnerCobuy: true, qualityAlert: true, organicRunner: true, convictionAlert: true, watchlistRunner: true, runnerAlert: true, themeAlert: true, techAlert: true, techFreshOnly: false, skipLowQuality: true, breakoutAlert: true, convictionDigest: true, githubActive: true, morningCall: true, tgSignal: true, ...(settingsQ.data.triggers || {}) });
    }
  }, [settingsQ.data]);

  // Live reveal count for the X Handle Radar score bar
  useEffect(() => {
    let alive = true;
    fetchHandleCount(clientId, 0).then((d) => alive && setHandleCounts((s) => ({ ...s, base: d.count, handles: d.handles }))).catch(() => {});
    return () => { alive = false; };
  }, [clientId]);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      fetchHandleCount(clientId, handleMinScore).then((d) => alive && setHandleCounts((s) => ({ ...s, preview: d.count, handles: d.handles }))).catch(() => {});
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [clientId, handleMinScore]);


  const saveMut = useMutation({
    mutationFn: () => saveAlertSettings(clientId, { chatId, enabled, minScore, egMinScore, clusterMin, millyMinSol, gradMaxMcap, handleMinScore, provenMinHit, digestHour, triggers, watchlist, platform, watchedCreators, robinhoodKeywords }),
    onSuccess: () => {
      toast.success("Alert settings saved");
      if (enabled) trackEvent("alert_optin", clientId, {});
    },
    onError: () => toast.error("Failed to save settings"),
  });

  const testMut = useMutation({
    mutationFn: async () => {
      await saveAlertSettings(clientId, { chatId, enabled, minScore, egMinScore, clusterMin, millyMinSol, gradMaxMcap, handleMinScore, provenMinHit, digestHour, triggers, watchlist, platform, watchedCreators, robinhoodKeywords });
      return sendTestAlert(clientId);
    },
    onSuccess: () => toast.success("Test message sent! Check Telegram."),
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to send test"),
  });

  const deployerTestMut = useMutation({
    mutationFn: async () => {
      await saveAlertSettings(clientId, { chatId, enabled, minScore, egMinScore, clusterMin, millyMinSol, gradMaxMcap, handleMinScore, provenMinHit, digestHour, triggers, watchlist, platform, watchedCreators, robinhoodKeywords });
      return sendDeployerTestAlert(clientId);
    },
    onSuccess: () => toast.success("Sample deployer alert sent! Check Telegram."),
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to send sample"),
  });

  const botConfigured = configQ.data?.botConfigured;

  return (
    <div className="max-w-[720px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center bg-neon text-black">
            <BellRingingIcon size={24} weight="bold" />
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">ALERTS</h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
          Get pinged on <span className="text-neon font-semibold">Telegram</span> the moment a coin crosses your Degen
          Score rules. The scanner checks live data every 2 minutes.
        </p>
      </div>

      {!configQ.isLoading && !botConfigured && (
        <div data-testid="bot-warning" className="border border-warn/30 bg-warn/5 p-4 flex gap-3">
          <LockKeyIcon size={20} className="text-warn shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            The Telegram bot isn't connected yet. Alerts will save but won't send until the app owner adds a bot token.
          </p>
        </div>
      )}

      {/* Connect */}
      <div className="border border-white/10 bg-card p-5 space-y-4">
        <h2 className="font-heading font-bold text-lg">1. Connect Telegram</h2>
        <ol className="text-sm text-muted-foreground space-y-1.5 list-decimal pl-4">
          <li>Open Telegram and search <span className="font-mono text-white">@userinfobot</span>, press Start — it replies with your numeric <span className="text-white">chat ID</span>.</li>
          <li>Message the app's alert bot once (press Start) so it's allowed to DM you.</li>
          <li>Paste your chat ID below and hit <span className="text-white">Send test</span>.</li>
        </ol>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="chatid-input"
            value={chatId}
            onChange={(e) => setChatId(e.target.value)}
            placeholder="Your Telegram chat ID (e.g. 123456789)"
            className="flex-1 bg-black/40 border border-white/10 px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-neon/50 transition-colors"
          />
          <button
            data-testid="test-alert-btn"
            onClick={() => { if (requireAuth("set up Telegram alerts")) testMut.mutate(); }}
            disabled={!chatId || testMut.isPending}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors disabled:opacity-40"
          >
            {testMut.isPending ? <SpinnerGapIcon size={16} className="animate-spin" /> : <PaperPlaneTiltIcon size={16} weight="bold" />}
            Send test
          </button>
        </div>
      </div>

      {/* Preferred buy platform */}
      <div className="border border-white/10 bg-card p-5 space-y-4">
        <div>
          <h2 className="font-heading font-bold text-lg">2. Preferred buy platform</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Every alert includes a <span className="text-neon">⚡ Buy</span> link. Pick the exchange your 1-click links should open.
          </p>
        </div>
        <div data-testid="platform-selector" className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              data-testid={`platform-option-${p.id}`}
              onClick={() => setPlatform(p.id)}
              className={`py-2.5 px-3 text-sm font-semibold border transition-colors ${
                platform === p.id
                  ? "bg-neon/15 border-neon/60 text-neon"
                  : "bg-black/40 border-white/10 text-muted-foreground hover:border-white/25 hover:text-white"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {platform === "robinhood" && (
          <p data-testid="robinhood-note" className="text-[11px] text-muted-foreground leading-relaxed">
            Robinhood links open the coin's page on <span className="text-neon">robinhood.com</span> by ticker — works for coins Robinhood has listed. Newer on-chain meme coins may not be tradable there yet.
          </p>
        )}
      </div>
      <div className="border border-white/10 bg-card p-5 space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg">3. Alert Rules</h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Enabled</span>
            <Toggle checked={enabled} onChange={setEnabled} testid="alerts-enabled-toggle" />
          </div>
        </div>

        <TriggerRow
          title="Degen Score threshold"
          desc={`Ping me when any coin's score is at or above my target.`}
          checked={triggers.threshold}
          onChange={(v) => setTriggers((t) => ({ ...t, threshold: v }))}
          testid="trigger-threshold"
        />

        <div className="py-4 border-b border-white/5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Score target</span>
            <span className="font-mono font-bold text-neon text-lg">{minScore}+</span>
          </div>
          <input
            data-testid="minscore-slider"
            type="range"
            min="40"
            max="95"
            step="5"
            value={minScore}
            onChange={(e) => setMinScore(Number(e.target.value))}
            className="w-full accent-neon"
          />
          <p className="text-[11px] text-muted-foreground mt-1">Score bar for your general alerts (higher = stricter).</p>
        </div>

        <div className="py-4 border-b border-white/5" data-testid="handle-minscore-section">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">🔎 X Handle Radar — score bar</span>
            <span className="font-mono font-bold text-neon text-lg">{handleMinScore === 0 ? "Default" : `${handleMinScore}+`}</span>
          </div>
          <input
            data-testid="handle-minscore-slider"
            type="range"
            min="0"
            max="90"
            step="5"
            value={handleMinScore}
            onChange={(e) => setHandleMinScore(Number(e.target.value))}
            className="w-full accent-neon"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            {handleMinScore === 0
              ? "Default filter (≈50–60) — the standard X Handle Radar bar."
              : `Showing handle coins scoring ${handleMinScore}+ and dropping the "older coins need 65+" rule.`}
            {" "}Set this <span className="text-neon">lower</span> than your general score so fewer handle coins get filtered. Separate from the score above.
          </p>
          {handleCounts.handles === 0 ? (
            <p className="text-[11px] text-muted-foreground/70 mt-1.5" data-testid="handle-count-hint">Add handles on the X Handle Radar page to see live counts here.</p>
          ) : handleCounts.preview != null ? (
            <p className="text-[11px] font-mono mt-1.5" data-testid="handle-count-live">
              <span className="text-neon">Shows ~{handleCounts.preview} coin{handleCounts.preview === 1 ? "" : "s"}</span>
              {handleCounts.base != null && handleCounts.preview > handleCounts.base && (
                <span className="text-amber-300"> (+{handleCounts.preview - handleCounts.base} vs default)</span>
              )}
              {handleCounts.base != null && handleCounts.preview < handleCounts.base && (
                <span className="text-muted-foreground"> ({handleCounts.preview - handleCounts.base} vs default)</span>
              )}
            </p>
          ) : null}
        </div>

        <div className="py-4 border-b border-white/5" style={{ background: "linear-gradient(90deg, rgba(0,229,153,0.08), transparent)" }}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-bold text-sm flex items-center gap-2 text-neon">🧹 Skip low-quality coins</div>
              <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                Keeps your pings clean — never alerts on coins caught by the <b>junk gate</b> (dead/thin liquidity, can't-exit ratios, dumping low-scorers). A real runner never looks like this, so you won't miss the big ones. Applies to score, run/dump, launch &amp; candle alerts.
              </div>
            </div>
            <Toggle checked={triggers.skipLowQuality} onChange={(v) => setTriggers((t) => ({ ...t, skipLowQuality: v }))} testid="trigger-skip-low-quality" />
          </div>
        </div>

        <TriggerRow
          title="New high-score launches"
          desc="Fresh coins (<72h old) that hit a score of 75+."
          checked={triggers.newHighScore}
          onChange={(v) => setTriggers((t) => ({ ...t, newHighScore: v }))}
          testid="trigger-newhigh"
        />
        <TriggerRow
          title="Watchlist score moves"
          desc={`Big score swings (±10) on your ${watchlist.length} watched coin${watchlist.length === 1 ? "" : "s"}.`}
          checked={triggers.watchlistChange}
          onChange={(v) => setTriggers((t) => ({ ...t, watchlistChange: v }))}
          testid="trigger-watchlist"
        />
        <TriggerRow
          title="Smart money trades"
          desc="Buys & sells from the wallets you track on the Smart Money page."
          checked={triggers.smartMoney}
          onChange={(v) => setTriggers((t) => ({ ...t, smartMoney: v }))}
          testid="trigger-smartmoney"
        />
        <TriggerRow
          title="🚀 Tracked wallet deploys"
          desc="Instant ping the second a wallet you track on Smart Money MINTS a new token — fires off the launch feed itself, so it works even in Eco/paused polling and skips every filter. The highest-signal event a tracked wallet can produce."
          checked={triggers.trackedDeploy}
          onChange={(v) => setTriggers((t) => ({ ...t, trackedDeploy: v }))}
          testid="trigger-tracked-deploy"
        />
        <TriggerRow
          title="🔕 Deploys only (mute routine buys)"
          desc="Max signal mode: silences the routine buy/sell pings above (Smart money trades + Copy buys) so tracked wallets only reach you when they DEPLOY. Milly's Call, exits and your other triggers keep their own switches."
          checked={triggers.deployOnly}
          onChange={(v) => setTriggers((t) => ({ ...t, deployOnly: v }))}
          testid="trigger-deploy-only"
        />
        <TriggerRow
          title="🌅 Morning dev launch digest"
          desc="One Telegram recap of every coin your saved devs (follows, Alpha creators, watched wallets) shipped overnight — with live early stats (mcap, volume, LP, 24h move) and each dev's 🎯 hit rate. Silent on days with nothing. Pick your delivery hour below. Zero Helius credits."
          checked={triggers.devDigest}
          onChange={(v) => setTriggers((t) => ({ ...t, devDigest: v }))}
          testid="trigger-dev-digest"
        />
        {triggers.devDigest && (
          <div className="flex flex-wrap items-center gap-3 pl-4 py-2 border-l-2 border-neon/30 ml-2" data-testid="digest-hour-row">
            <span className="text-sm text-muted-foreground">Deliver at</span>
            <select
              value={digestHour}
              onChange={(e) => setDigestHour(Number(e.target.value))}
              data-testid="digest-hour-select"
              className="bg-background border border-border rounded-md px-2 py-1 text-sm font-mono"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, "0")}:00 UTC</option>
              ))}
            </select>
            <span className="text-xs text-muted-foreground">(13:00 UTC ≈ 9am ET)</span>
            <button
              type="button"
              data-testid="send-test-digest-btn"
              onClick={async () => {
                try {
                  await saveAlertSettings(clientId, { chatId, enabled, minScore, egMinScore, clusterMin, millyMinSol, gradMaxMcap, handleMinScore, provenMinHit, digestHour, triggers, watchlist, platform, watchedCreators, robinhoodKeywords });
                  await sendTestDigest(clientId);
                  toast.success("Digest sent — check your Telegram");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't send digest");
                }
              }}
              className="text-xs font-semibold text-neon border border-neon/40 rounded-md px-2.5 py-1 hover:bg-neon/10 transition-colors"
            >
              Send now ↗
            </button>
          </div>
        )}
        <TriggerRow
          title="🛰 Insider Radar auto-track"
          desc="When a wallet has been a top-10 early holder in 3+ bonding-curve graduates with at least one $1M+ runner, it's added to your Smart Money tracking automatically (and you get a Telegram ping). Respects your 200-wallet cap."
          checked={triggers.radarAutoTrack}
          onChange={(v) => setTriggers((t) => ({ ...t, radarAutoTrack: v }))}
          testid="trigger-radar-autotrack"
        />
        <TriggerRow
          title="🎯 Tier 3 Strong Confirmation"
          desc="A young coin (≤48h) hits 70+ on the Tier 3 overlay — degen score + conviction building early, survivor/organic flags, and KOLs posting the CA. Pings once per coin."
          checked={triggers.tier3Confirm}
          onChange={(v) => setTriggers((t) => ({ ...t, tier3Confirm: v }))}
          testid="trigger-tier3-confirm"
        />
        <TriggerRow
          title="🚪 Waiting Room entry"
          desc="A new coin just got admitted to the Waiting Room with a strong score — ping includes mcap, liquidity, info badges and dev record. Score floor is tunable on the Waiting Room page (default 50)."
          checked={triggers.waiterEntry}
          onChange={(v) => setTriggers((t) => ({ ...t, waiterEntry: v }))}
          testid="trigger-waiter-entry"
        />
        <TriggerRow
          title="⏰ Waiter wake-up"
          desc="Waiting Room coins (10-50K mcap, real token info, hasn't run yet) that suddenly wake up — +30% in 1h with 3x volume. Pings you and auto-adds the coin to your Watchlist."
          checked={triggers.waiterWake}
          onChange={(v) => setTriggers((t) => ({ ...t, waiterWake: v }))}
          testid="trigger-waiter-wake"
        />

        <div className="py-4 border-b border-white/5" style={{ background: "linear-gradient(90deg, rgba(240,192,0,0.08), transparent)" }}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-bold text-sm flex items-center gap-2" style={{ color: "#F0C000" }}>
                💰 MILLY'S CALL — big-money buys
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                The strongest "this is going to millions" tell. Ping when a tracked wallet throws <b>real size</b> at a token — a single whale buy <i>or</i> stacking buys (e.g. 10 → 10 → 20 SOL) that add up past your threshold. Cuts through the 0.1 SOL white noise.
              </div>
            </div>
            <Toggle checked={triggers.millysCall} onChange={(v) => setTriggers((t) => ({ ...t, millysCall: v }))} testid="trigger-millys-call" />
          </div>
        </div>

        <div className={`py-4 border-b border-white/5 ${triggers.millysCall ? "" : "opacity-50"}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Minimum buy size to alert</span>
            <span className="font-mono font-bold text-lg" style={{ color: "#F0C000" }}>{millyMinSol}+ SOL</span>
          </div>
          <input
            data-testid="milly-minsol-slider"
            type="range"
            min="5"
            max="50"
            step="5"
            value={millyMinSol}
            onChange={(e) => setMillyMinSol(Number(e.target.value))}
            disabled={!triggers.millysCall}
            className="w-full"
            style={{ accentColor: "#F0C000" }}
          />
          <p className="text-xs text-muted-foreground mt-1.5">
            Re-pings as the same wallet stacks higher (e.g. 5+ → 15+ → 30+ SOL on one token within 6h). Raise it to only hear about serious conviction.
          </p>
        </div>

        <div className="py-4 border-b border-white/5" style={{ background: "linear-gradient(90deg, rgba(240,192,0,0.12), transparent)" }}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-bold text-sm flex items-center gap-2" style={{ color: "#F0C000" }}>
                🏃💰 MILLY RUNNER CALL — the convergence super-signal
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                Your <b>highest-signal, lowest-noise</b> ping: fires ONLY when a coin is <b>running</b> (volume surge + buy pressure) <i>and</i> a
                tracked wallet is dropping big SOL on it at the same time. Smart money loading up on a coin that's already moving. Uses your
                min-SOL threshold above. Turn off Milly's Call and keep this on to hear <i>only</i> these.
              </div>
            </div>
            <Toggle checked={triggers.millyRunner} onChange={(v) => setTriggers((t) => ({ ...t, millyRunner: v }))} testid="trigger-milly-runner" />
          </div>
        </div>

        <TriggerRow
          title="🎓 Fresh Graduate Runner"
          desc="The early multi-M catcher. Ping when a pump.fun token GRADUATES the bonding curve and is already ripping — accelerating volume + buy pressure while still under your max market cap below. Catches runners before they hit the mainstream Radar."
          checked={triggers.graduateRunner}
          onChange={(v) => setTriggers((t) => ({ ...t, graduateRunner: v }))}
          testid="trigger-graduate-runner"
        />

        <div className={`py-4 border-b border-white/5 ${triggers.graduateRunner ? "" : "opacity-50"}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Max market cap ("still early" ceiling)</span>
              <button
                type="button"
                data-testid="grad-mcap-default-btn"
                onClick={() => setGradMaxMcap(750000)}
                disabled={!triggers.graduateRunner}
                title="Reset to the recommended default ($750K)"
                className="text-[10px] font-mono px-1.5 py-0.5 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 transition-colors disabled:opacity-40"
              >
                Default
              </button>
            </div>
            <span className="font-mono font-bold text-cyan-300 text-lg" data-testid="grad-mcap-value">
              ${(gradMaxMcap / 1000).toFixed(0)}K
            </span>
          </div>
          <input
            data-testid="grad-mcap-slider"
            type="range"
            min="250000"
            max="1500000"
            step="50000"
            value={gradMaxMcap}
            onChange={(e) => setGradMaxMcap(Number(e.target.value))}
            disabled={!triggers.graduateRunner}
            className="w-full accent-cyan-400"
          />
          <div className="flex justify-between text-[10px] font-mono text-muted-foreground mt-1">
            <span>$250K · earliest</span>
            <span>$1.5M · looser</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
            Only alert while a fresh graduate is still under this cap — lower = catch them earlier &amp; cheaper, higher = catch more runners. Default <b>$750K</b>.
          </div>
        </div>

        <TriggerRow
          title="🎯 Cluster buy (smart-money convergence)"
          desc="Ping when multiple of your tracked wallets buy the SAME token within 6h — a high-conviction 'something big is launching' signal. Re-pings as more wallets pile in (e.g. 2 → 4 → 6)."
          checked={triggers.clusterBuy}
          onChange={(v) => setTriggers((t) => ({ ...t, clusterBuy: v }))}
          testid="trigger-cluster-buy"
        />

        <div className={`py-4 border-b border-white/5 ${triggers.clusterBuy ? "" : "opacity-50"}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Wallets needed for a cluster</span>
            <span className="font-mono font-bold text-neon text-lg">{clusterMin}+</span>
          </div>
          <input
            data-testid="cluster-min-slider"
            type="range"
            min="2"
            max="8"
            step="1"
            value={clusterMin}
            onChange={(e) => setClusterMin(Number(e.target.value))}
            disabled={!triggers.clusterBuy}
            className="w-full accent-neon"
          />
          <p className="text-xs text-muted-foreground mt-1.5">
            Start at 2 to catch convergence early; raise it to 3–4 if you get too many low-signal pings.
          </p>
        </div>
        <TriggerRow
          title="🔥 Copy-wallet buys only (proven wallets)"
          desc="Ping only when a high-performing wallet (PnL score ≥60, your 🔥 COPY wallets) makes a BUY. Great low-noise signal to copy-trade your best trackers."
          checked={triggers.copyBuys}
          onChange={(v) => setTriggers((t) => ({ ...t, copyBuys: v }))}
          testid="trigger-copy-buys"
        />
        <TriggerRow
          title="🌱 Organic runner (2nd wind — free, no wallet tracking)"
          desc="The real money-maker pattern — pings when a token that shook out hard from its peak (e.g. 400k → 100k) starts running again with buyers back in. Catches organic survivors reclaiming, not fresh pumps. Zero Helius."
          checked={triggers.organicRunner}
          onChange={(v) => setTriggers((t) => ({ ...t, organicRunner: v }))}
          testid="trigger-organic-runner"
        />
        <TriggerRow
          title="⭐🌱 Watchlist runner (your saved coins)"
          desc="Ping when a coin YOU saved to your watchlist starts its 2nd-wind run (shook out then bouncing back with buyers). So you don't have to keep checking the ones you're watching. Zero Helius."
          checked={triggers.watchlistRunner}
          onChange={(v) => setTriggers((t) => ({ ...t, watchlistRunner: v }))}
          testid="trigger-watchlist-runner"
        />
        <TriggerRow
          title="🚀🌅 Early Runner (catch the day's runner EARLY)"
          desc="The rare, high-value ping (only a few a day): a young token getting bought up with accelerating real volume and buyers in control — the day-runner pattern caught in its first hour. Judged on volume & buyers, not noisy price %. Zero Helius."
          checked={triggers.earlyRunner}
          onChange={(v) => setTriggers((t) => ({ ...t, earlyRunner: v }))}
          testid="trigger-early-runner"
        />
        <TriggerRow
          title="🏆🚀 Today's Runner (high Runner Score)"
          desc="Ping the moment a fresh, organic survivor crosses a high Runner Score — the daily-runner pattern forming early (fits the day's hot theme, high conviction, momentum building). Catch the day's runner the second it forms. Zero Helius."
          checked={triggers.runnerAlert}
          onChange={(v) => setTriggers((t) => ({ ...t, runnerAlert: v }))}
          testid="trigger-runner-alert"
        />
        <TriggerRow
          title="📈 Carry — the next one (h6≠h24, still rising)"
          desc="Ping when a token survives its launch candle and keeps climbing — 24h & 6h have diverged, the move is still positive, and velocity is rising this hour. The 'spot the next one' signal. Zero Helius."
          checked={triggers.candleCarry}
          onChange={(v) => setTriggers((t) => ({ ...t, candleCarry: v }))}
          testid="trigger-candle-carry"
        />
        <TriggerRow
          title="🕯️🔥 Launch Candle (brand-new, first minutes)"
          desc="Ping the earliest possible moment — when a fresh token launches into a single candle (h6 = h24) with real heat. Highest-risk, highest-reward: these are minutes old. Off by default. Zero Helius."
          checked={triggers.candleLaunch}
          onChange={(v) => setTriggers((t) => ({ ...t, candleLaunch: v }))}
          testid="trigger-candle-launch"
        />
        <TriggerRow
          title="🚀 Breakout runner (the CYBERLEEK pattern)"
          desc="Ping when a quality survivor explodes — a token past its launch (age 6h+), with real liquidity & socials, whose 6h volume is running well above its daily pace, price breaking out 40%+ with buyers in control. Also covers ♻️ second-leg reclaims (dumped hard from a high, now recovering with fresh volume). Respects Skip-low-quality."
          checked={triggers.breakoutAlert}
          onChange={(v) => setTriggers((t) => ({ ...t, breakoutAlert: v }))}
          testid="trigger-breakout-alert"
        />
        <TriggerRow
          title="🏆 Highest-Conviction daily digest"
          desc="Once a day (~9am ET) get a Telegram recap of every Highest-Conviction coin that fired the Handle + Breakout double-signal in the last 24h — so you never miss one that popped overnight."
          checked={triggers.convictionDigest}
          onChange={(v) => setTriggers((t) => ({ ...t, convictionDigest: v }))}
          testid="trigger-conviction-digest"
        />
        <TriggerRow
          title="🔧 Dev is building (GitHub Radar)"
          desc="Ping when a trending token's linked GitHub is real and recently pushed — a dev actually shipping code. Rare in memecoins, so it's a strong legitimacy signal (not a buy signal)."
          checked={triggers.githubActive}
          onChange={(v) => setTriggers((t) => ({ ...t, githubActive: v }))}
          testid="trigger-github-active"
        />
        <TriggerRow
          title="☕ The Morning Call (daily brief)"
          desc="A once-a-day recap (~9am ET) of everything that mattered while you slept — the night's breakouts, highest-conviction coins & survivor pops across every radar, with the one to look at."
          checked={triggers.morningCall}
          onChange={(v) => setTriggers((t) => ({ ...t, morningCall: v }))}
          testid="trigger-morning-call"
        />
        <TriggerRow
          title="🔔 Called on Telegram (Signal Scanner)"
          desc="Ping the moment a coin gets called in one of the public Telegram channels you watch on the Signal Scanner page — tagged with the exact channel. Requires the scanner to be connected."
          checked={triggers.tgSignal}
          onChange={(v) => setTriggers((t) => ({ ...t, tgSignal: v }))}
          testid="trigger-tg-signal"
        />
        <TriggerRow
          title="🔥🆕 New theme spiking (front-run the meta)"
          desc="Ping when a brand-new theme suddenly clusters on the Radar (e.g. cats, frogs, a name) that wasn't there a few hours ago — so you're first into the next meta and can hunt the cleanest survivor. Zero Helius."
          checked={triggers.themeAlert}
          onChange={(v) => setTriggers((t) => ({ ...t, themeAlert: v }))}
          testid="trigger-theme-alert"
        />
        <TriggerRow
          title="🔬🚀 Fresh tech launch (website + real socials)"
          desc="Ping when a brand-new token launches looking like a real product — it ships a website AND real socials (X/Telegram), not just a meme. Catch tech plays at launch. Zero Helius."
          checked={triggers.techAlert}
          onChange={(v) => setTriggers((t) => ({ ...t, techAlert: v }))}
          testid="trigger-tech-alert"
        />
        {triggers.techAlert && (
          <div className="pl-6 border-l-2 border-[#22D3EE]/30 ml-1">
            <TriggerRow
              title="⚡ Brand-new only (under 6h old)"
              desc="Only ping for tech tokens under 6 hours old — so you catch product launches at the very start and skip older tech coins already on the board."
              checked={triggers.techFreshOnly}
              onChange={(v) => setTriggers((t) => ({ ...t, techFreshOnly: v }))}
              testid="trigger-tech-fresh-only"
            />
          </div>
        )}
        <TriggerRow
          title="🏆 Quality runner (free — no wallet tracking)"
          desc="Ping the moment a fresh launch crosses into 'Quality' — it lasted (Survivor: real liquidity, still trading, not dumped) AND whales are actively buying it. Zero Helius, catches the free-signal runners the second they qualify."
          checked={triggers.qualityAlert}
          onChange={(v) => setTriggers((t) => ({ ...t, qualityAlert: v }))}
          testid="trigger-quality-alert"
        />
        <TriggerRow
          title="👀 ANSEM-tier conviction (free — no wallet tracking)"
          desc="Ping the moment a fresh coin crosses a high Conviction score (≥75) — a credibility-blind read on pure volume, survival & buy pressure. Catches hidden runners early, even when their token info looks weak (the ANSEM pattern). Zero Helius."
          checked={triggers.convictionAlert}
          onChange={(v) => setTriggers((t) => ({ ...t, convictionAlert: v }))}
          testid="trigger-conviction-alert"
        />
        <TriggerRow
          title="🕵️ Cabal loading (coordinated group)"
          desc="Ping when 3+ wallets from a KNOWN cabal (a crew that keeps buying the same tokens together) ape a fresh coin — get in with the group, not after them. Solana only."
          checked={triggers.cabalBuy}
          onChange={(v) => setTriggers((t) => ({ ...t, cabalBuy: v }))}
          testid="trigger-cabal-buy"
        />
        <TriggerRow
          title="🏆 Recurring-winner co-buy"
          desc="Ping when 2+ proven winners (PnL score ≥75) buy the same new token together — the highest-conviction 'smart hands agree' signal."
          checked={triggers.winnerCobuy}
          onChange={(v) => setTriggers((t) => ({ ...t, winnerCobuy: v }))}
          testid="trigger-winner-cobuy"
        />
        <TriggerRow
          title="📉 Smart money exit / distribution"
          desc="The flip side of the buys — ping when a tracked wallet dumps a position (≥ your Milly min SOL), escalated when 2+ tracked wallets sell the same token. Know when to get OUT."
          checked={triggers.smartExit}
          onChange={(v) => setTriggers((t) => ({ ...t, smartExit: v }))}
          testid="trigger-smart-exit"
        />
        <TriggerRow
          title="🚀 Coin running (volume surge + buyers)"
          desc="Ping when a coin's volume spikes 3x+ its hourly average with buy pressure."
          checked={triggers.runAlert}
          onChange={(v) => setTriggers((t) => ({ ...t, runAlert: v }))}
          testid="trigger-run"
        />
        <TriggerRow
          title="🔻 Coin dumping (volume surge + sellers)"
          desc="Ping when a coin's volume spikes with heavy selling."
          checked={triggers.dumpAlert}
          onChange={(v) => setTriggers((t) => ({ ...t, dumpAlert: v }))}
          testid="trigger-dump"
        />
        <TriggerRow
          title="🚀 Launchpad — pump.fun about to migrate"
          desc="Ping when a live pump.fun coin crosses 80% bonding curve with rising buy velocity (10+ buys in 5m)."
          checked={triggers.launchAlert}
          onChange={(v) => setTriggers((t) => ({ ...t, launchAlert: v }))}
          testid="trigger-launch"
        />
        <TriggerRow
          title="⭐ Alpha Dev launch (instant)"
          desc="Ping the moment a creator on your Alpha Devs list mints a brand-new pump.fun coin — before it hits the Radar."
          checked={triggers.alphaLaunch}
          onChange={(v) => setTriggers((t) => ({ ...t, alphaLaunch: v }))}
          testid="trigger-alpha-launch"
        />
        <TriggerRow
          title="⭐ Proven deployer launch (instant)"
          desc="Ping the moment ANY deployer with a proven runner track record ($1M+ / $5M+ past coins) mints a brand-new pump.fun coin — before it hits DEX or the Radar. High bar, low noise."
          checked={triggers.provenDeployerLaunch}
          onChange={(v) => setTriggers((t) => ({ ...t, provenDeployerLaunch: v }))}
          testid="trigger-proven-deployer-launch"
          action={triggers.provenDeployerLaunch && (
            <div className="mt-3 space-y-2 max-w-md">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-medium">Minimum hit rate</span>
                <span className="font-mono font-bold text-neon text-sm" data-testid="proven-min-hit-value">{provenMinHit === 0 ? "Any proven dev" : `${provenMinHit}%+`}</span>
              </div>
              <input
                type="range" min="0" max="80" step="5"
                value={provenMinHit}
                onChange={(e) => setProvenMinHit(Number(e.target.value))}
                className="w-full accent-[#00E599]"
                data-testid="proven-min-hit-slider"
              />
              <div className="text-[11px] text-muted-foreground leading-relaxed">
                {provenMinHit === 0
                  ? "Pings on every proven deployer (any past $1M+ runner). Raise it to only hear from the elite."
                  : `Only pings when the deployer's past hit rate (runners ÷ total coins) is ${provenMinHit}% or higher — pure signal. Also applies to your 🌟 Followed builder pings.`}
              </div>
              <button
              data-testid="deployer-test-btn"
              onClick={() => { if (requireAuth("test deployer alerts")) deployerTestMut.mutate(); }}
              disabled={!chatId || deployerTestMut.isPending}
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-neon/40 text-neon hover:bg-neon/10 transition-colors disabled:opacity-40"
              title={!chatId ? "Add your Telegram chat ID above first" : "Send a sample of this alert to your Telegram"}
            >
              {deployerTestMut.isPending ? <SpinnerGapIcon size={13} className="animate-spin" /> : <PaperPlaneTiltIcon size={13} weight="bold" />}
              Send me a sample
            </button>
            </div>
          )}
        />
        <TriggerRow
          title="🌟 Followed builder launch (instant)"
          desc="Get pinged the moment a deployer YOU follow mints a brand-new coin — off the live pump.fun stream PLUS an on-chain sweeper that checks your devs' wallets every few minutes (catches launches on any launchpad). Your min hit-rate slider above also gates these pings."
          checked={triggers.followedBuilderLaunch}
          onChange={(v) => setTriggers((t) => ({ ...t, followedBuilderLaunch: v }))}
          testid="trigger-followed-builder-launch"
        />
        <TriggerRow
          title="⛔ Blacklisted dev buy"
          desc="Ping when a tracked smart-money wallet buys a coin made by a dev you've flagged as a rugger."
          checked={triggers.blacklistBuy}
          onChange={(v) => setTriggers((t) => ({ ...t, blacklistBuy: v }))}
          testid="trigger-blacklist-buy"
        />
        <TriggerRow
          title="🎲 Early Gambles (fresh low-caps)"
          desc="Ping when a brand-new low-cap (young, $15k–$500k mcap, already showing volume) clears your Degen Score below — the freshest movers before the crowd finds them."
          checked={triggers.earlyGamble}
          onChange={(v) => setTriggers((t) => ({ ...t, earlyGamble: v }))}
          testid="trigger-early-gamble"
        />

        <div className={`py-4 border-b border-white/5 ${triggers.earlyGamble ? "" : "opacity-50"}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Early Gamble score target</span>
            <span className="font-mono font-bold text-warn text-lg">{egMinScore}+</span>
          </div>
          <input
            data-testid="eg-minscore-slider"
            type="range"
            min="30"
            max="90"
            step="5"
            value={egMinScore}
            onChange={(e) => setEgMinScore(Number(e.target.value))}
            disabled={!triggers.earlyGamble}
            className="w-full accent-warn"
          />
        </div>

        <TriggerRow
          title="⚡ Fast Mover (2× pump / 50% dump this hour)"
          desc="Ping when an Early Gamble moves violently in one hour — 🚀 PUMP at 2×+ (+100%) or 🔻 DUMP at -50%+. Deduped per direction so a whipsawing coin won't spam you."
          checked={triggers.fastMover}
          onChange={(v) => setTriggers((t) => ({ ...t, fastMover: v }))}
          testid="trigger-fast-mover"
        />
        <TriggerRow
          title="👁️ Coins by a specific creator"
          desc="Ping the moment a deployer wallet you list mints a brand-new pump.fun coin — even unproven ones. Paste any Solana creator wallet address below."
          checked={triggers.watchedCreatorLaunch}
          onChange={(v) => setTriggers((t) => ({ ...t, watchedCreatorLaunch: v }))}
          testid="trigger-watched-creator"
        />
        <div className="pl-1 pr-1 -mt-1" data-testid="watched-creators-box">
          <div className="flex gap-2">
            <input
              data-testid="watched-creators-input"
              value={creatorInput}
              onChange={(e) => setCreatorInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCreators(); } }}
              placeholder="Paste a Solana creator wallet, then Add"
              className="flex-1 bg-black/40 border border-white/12 focus:border-neon/50 outline-none text-xs font-mono text-white p-2.5 placeholder:text-muted-foreground/50"
            />
            <button
              data-testid="watched-creators-add-btn"
              onClick={addCreators}
              disabled={!creatorInput.trim()}
              className="px-4 py-2 text-xs font-bold bg-neon text-black disabled:opacity-40 shrink-0"
            >
              + Add
            </button>
          </div>
          {watchedCreators.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="watched-creators-list">
              {watchedCreators.map((w) => (
                <span key={w} data-testid={`watched-creator-${w}`} className="flex items-center gap-1.5 px-2 py-1 border border-neon/40 bg-neon/5 font-mono text-[11px] text-white">
                  {w.slice(0, 4)}…{w.slice(-4)}
                  <button
                    data-testid={`watched-creator-remove-${w}`}
                    onClick={() => setWatchedCreators((list) => list.filter((x) => x !== w))}
                    title="Remove"
                    className="text-muted-foreground hover:text-danger font-bold"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <p className="text-[10px] text-muted-foreground mt-1.5">
            {watchedCreators.length} wallet{watchedCreators.length === 1 ? "" : "s"} watched · new launches only (from the moment you save) · <b className="text-neon">Save below to apply.</b>
          </p>
        </div>

        <TriggerRow
          title="🇺🇸 Robinhood Chain launch watch (keywords)"
          desc="Robinhood Chain is a separate Ethereum L2 — Solana wallets don't exist there. Instead of a wallet, this watches the whole chain and pings the second a BRAND-NEW token appears whose name/ticker matches your keywords below (e.g. a $TRUTH launch). Existing tokens are baselined on save, so you only get NEW ones."
          checked={triggers.robinhoodLaunch}
          onChange={(v) => setTriggers((t) => ({ ...t, robinhoodLaunch: v }))}
          testid="trigger-robinhood-launch"
        />
        <div className="pl-1 pr-1 -mt-1" data-testid="robinhood-keywords-box">
          <textarea
            data-testid="robinhood-keywords-input"
            value={robinhoodKeywords.join("\n")}
            onChange={(e) => setRobinhoodKeywords(e.target.value.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean))}
            placeholder={"One keyword per line — matches token name or ticker\ne.g. truth\ntrump\ndjt"}
            rows={Math.min(8, Math.max(3, robinhoodKeywords.length + 1))}
            className="w-full bg-black/40 border border-white/12 focus:border-neon/50 outline-none text-xs font-mono text-white p-2.5 resize-y placeholder:text-muted-foreground/50"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            {robinhoodKeywords.length} keyword{robinhoodKeywords.length === 1 ? "" : "s"} watched on Robinhood Chain · new tokens only (baselined on save) · ⚠️ copycats will use these words too — verify before aping.
          </p>
        </div>

        <TriggerRow
          title="🇺🇸 Robinhood wallet moves (tracked 0x wallets)"
          desc="Pings when a wallet you track on the Robinhood Feed page acquires or deploys a BRAND-NEW token on Robinhood Chain — the real 'Trump team just aped/launched something' signal, straight from the source wallet. Existing holdings are baselined so you only get new moves."
          checked={triggers.robinhoodWallet}
          onChange={(v) => setTriggers((t) => ({ ...t, robinhoodWallet: v }))}
          testid="trigger-robinhood-wallet"
        />

        <TriggerRow
          title="🏗 Structure Gate"
          desc="Auto-skip alerts for tokens with UNLOCKED LP or a live mint authority — the two structural red flags that make a token pullable/printable. Applies to threshold, quality-runner, early-runner and graduate alerts."
          checked={triggers.structureGate}
          onChange={(v) => setTriggers((t) => ({ ...t, structureGate: v }))}
          testid="trigger-structure-gate"
        />

        <TriggerRow
          title="🔥 Robinhood holder surge"
          desc="Pings when ANY token on Robinhood Chain starts gaining holders unusually fast (+150/hr pace, at least +30 in the last hour) — the 'something is being aped right now' siren. One ping per token per 12h."
          checked={triggers.robinhoodSurge}
          onChange={(v) => setTriggers((t) => ({ ...t, robinhoodSurge: v }))}
          testid="trigger-robinhood-surge"
        />

        <TriggerRow
          title="🏁 Holder milestone (Pre-Existing Catch)"
          desc="One-time ping when an already-live Robinhood Chain coin crosses a holder milestone (100, 250, 500, 1K, 2.5K, 5K, 10K, 25K) — catches late movers the launch alerts missed. Each milestone pings exactly once per coin."
          checked={triggers.holderMilestone}
          onChange={(v) => setTriggers((t) => ({ ...t, holderMilestone: v }))}
          testid="trigger-holder-milestone"
        />

        <TriggerRow
          title="🤖 Bot-heavy warning (watchlist)"
          desc="Ping when a coin on your watchlist looks wash-traded (lots of tiny balanced trades, volume dwarfing liquidity) — fake volume is a classic pump-and-dump setup. Off by default."
          checked={triggers.botWarn}
          onChange={(v) => setTriggers((t) => ({ ...t, botWarn: v }))}
          testid="trigger-bot-warn"
        />

        <div className="pt-4">
          <button
            data-testid="save-alerts-btn"
            onClick={() => { if (requireAuth("save alerts")) saveMut.mutate(); }}
            disabled={saveMut.isPending}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 border border-neon/50 text-neon font-semibold text-sm hover:bg-neon/10 transition-colors disabled:opacity-40"
          >
            {saveMut.isPending ? <SpinnerGapIcon size={16} className="animate-spin" /> : <CheckCircleIcon size={18} weight="bold" />}
            Save alert settings
          </button>
        </div>
      </div>

      <div className="border border-white/10 bg-card p-4 flex gap-3">
        <WarningIcon size={18} className="text-muted-foreground shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Alerts are a research aid, not financial advice. Settings are tied to this browser. To receive DMs you must
          press Start on the bot first, otherwise Telegram blocks the message.
        </p>
      </div>
    </div>
  );
}
